<template>
  <div class="portrait-page" v-loading="loading">
    <div class="portrait-header">
      <div class="title-wrap">
        <div class="title-eyebrow">Student Portrait</div>
        <div class="title">学情画像</div>
        <div class="title-sub">
          {{
            dateRange && dateRange.length === 2
              ? `${dateRange[0]} 至 ${dateRange[1]}`
              : "默认近30天窗口"
          }}
        </div>
      </div>
      <div class="filters">
        <el-select
          v-model="courseSerialnumber"
          clearable
          size="small"
          placeholder="全部课程"
          style="width: 220px"
        >
          <el-option
            v-for="item in courseOptions"
            :key="item.serialnumber"
            :label="item.catalog"
            :value="item.serialnumber"
          />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          size="small"
        />
        <el-button type="primary" size="small" @click="loadPortrait"
          >查询</el-button
        >
      </div>
    </div>

    <div v-if="portrait && portrait.summary" class="section section--summary">
      <div class="section-head">
        <div class="section-title">总览指标</div>
        <div class="section-note">核心学习表现与风险信号</div>
      </div>
      <div class="summary-layout">
        <div class="summary-main">
          <div class="summary-grid">
            <div class="metric-item">
              <span>刷题作答</span>
              <strong>{{ portrait.summary.practiceAnsweredCount || 0 }}</strong>
            </div>
            <div class="metric-item">
              <span>刷题正确率</span>
              <strong>{{
                formatPercent(portrait.summary.practiceAccuracy)
              }}</strong>
            </div>
            <div class="metric-item">
              <span>自测次数</span>
              <strong>{{ portrait.summary.selfTestCount || 0 }}</strong>
            </div>
            <div class="metric-item">
              <span>自测客观正确率</span>
              <strong>{{
                formatPercent(portrait.summary.selfTestObjectiveAccuracy)
              }}</strong>
            </div>
            <div class="metric-item">
              <span>错题去重</span>
              <strong>{{ portrait.summary.wrongDistinctCount || 0 }}</strong>
            </div>
            <div class="metric-item">
              <span>错题累计</span>
              <strong>{{ portrait.summary.wrongTotalCount || 0 }}</strong>
            </div>
            <div class="metric-item">
              <span>错题订正</span>
              <strong>{{ portrait.summary.wrongCorrectionCount || 0 }}</strong>
            </div>
            <div class="metric-item metric-item--risk">
              <span>高风险标签</span>
              <strong>{{ portrait.summary.highRisk ? "是" : "否" }}</strong>
            </div>
          </div>
        </div>
        <div class="summary-ai">
          <div class="summary-ai-title">AI诊断建议</div>
          <div class="summary-ai-status">
            <el-tag size="mini" :type="aiTagType(portrait.aiDiagnosis)">
              {{
                (portrait.aiDiagnosis && portrait.aiDiagnosis.status) ||
                  "DISABLED"
              }}
            </el-tag>
          </div>
          <div
            class="summary-ai-content"
            v-html="
              formatAiSuggestion(
                (portrait.aiDiagnosis && portrait.aiDiagnosis.content) ||
                  '暂无 AI 诊断建议'
              )
            "
          ></div>
        </div>
      </div>
    </div>

    <div v-if="portrait" class="section section--trend">
      <div class="section-head">
        <div class="section-title">30天趋势</div>
        <div class="section-note">当前展示最近10天行为变化</div>
      </div>
      <div class="trend-shell">
        <div ref="trendChart" class="trend-chart"></div>
      </div>
    </div>

    <div v-if="portrait" class="section section--weak">
      <div class="section-head">
        <div class="section-title">薄弱知识点 TOP</div>
        <div class="section-note">风险分由错题占比与订正效果综合计算</div>
      </div>
      <div class="section-scroll">
        <el-table :data="portrait.weakKnowledgeTop || []" size="mini" border>
          <el-table-column
            prop="knowledgeName"
            label="知识点"
            min-width="180"
          />
          <el-table-column
            prop="wrongCount"
            label="错题权重次数"
            min-width="120"
          />
          <el-table-column label="错题占比" min-width="100">
            <template slot-scope="scope">
              {{ formatRatio(scope.row.wrongRate) }}
            </template>
          </el-table-column>
          <el-table-column label="订正正确率" min-width="110">
            <template slot-scope="scope">
              {{ formatRatio(scope.row.correctionRate) }}
            </template>
          </el-table-column>
          <el-table-column prop="riskScore" label="风险分" min-width="90" />
        </el-table>
      </div>
    </div>

    <div v-if="portrait" class="section section--tasks">
      <div class="section-head">
        <div class="section-title">任务/考试指标</div>
        <div class="section-note">进度、完成率与正确率</div>
      </div>
      <div class="task-grid">
        <div
          v-for="item in portrait.taskExamMetrics || []"
          :key="item.code"
          :class="[
            'task-item',
            `task-item--${String(item.code || '').toLowerCase()}`
          ]"
        >
          <div class="task-name">{{ item.name }}</div>
          <div>
            完成：{{ item.completedCount || 0 }} / {{ item.totalCount || 0 }}
          </div>
          <div>完成率：{{ formatPercent(item.completionRate) }}</div>
          <div>正确率：{{ formatPercent(item.accuracy) }}</div>
        </div>
      </div>
    </div>

    <div v-if="portrait" class="section section--diagnosis">
      <div class="section-head">
        <div class="section-title">诊断建议</div>
        <div class="section-note">依据行为数据生成的学习建议</div>
      </div>
      <div class="diagnosis-list">
        <div
          v-for="item in portrait.diagnosis || []"
          :key="item.code"
          class="diagnosis-item"
        >
          <el-tag size="mini" :type="diagnosisTagType(item.level)">{{
            item.level
          }}</el-tag>
          <span class="diagnosis-title">{{ item.title }}</span>
          <span class="diagnosis-desc">{{ item.description }}</span>
        </div>
      </div>
      <div class="section-title sub-title">下一步动作</div>
      <div class="hint-list">
        <div
          v-for="item in portrait.interventionHints || []"
          :key="item.actionType"
          class="hint-item"
        >
          <strong>{{ item.title }}</strong>
          <span>{{ item.description }}</span>
        </div>
      </div>
    </div>

    <el-empty v-if="!loading && !portrait" description="暂无画像数据" />
  </div>
</template>

<script>
import { getCourseByStudent } from "@/api/course";
import { getMyPortrait } from "@/api/question";
import * as echarts from "echarts";

export default {
  name: "StudentPortraitIndex",
  computed: {
    trendDisplayList() {
      const source = (this.portrait && this.portrait.trend) || [];
      if (!Array.isArray(source) || source.length <= 10) {
        return source;
      }
      const sorted = [...source].sort((a, b) => {
        const aTime = a && a.date ? new Date(a.date).getTime() : 0;
        const bTime = b && b.date ? new Date(b.date).getTime() : 0;
        return aTime - bTime;
      });
      return sorted.slice(-10);
    }
  },
  data() {
    return {
      loading: false,
      courseOptions: [],
      courseSerialnumber: "",
      dateRange: [],
      portrait: null,
      trendChart: null
    };
  },
  mounted() {
    this.initDefaultRange();
    this.loadCoursesAndPortrait();
    window.addEventListener("resize", this.resizeTrendChart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeTrendChart);
    this.disposeTrendChart();
  },
  methods: {
    initDefaultRange() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29);
      this.dateRange = [this.formatDate(start), this.formatDate(end)];
    },
    async loadCoursesAndPortrait() {
      try {
        const res = await getCourseByStudent();
        if (res.code === 200) {
          this.courseOptions = res.data || [];
        } else {
          this.courseOptions = [];
        }
      } catch (error) {
        this.courseOptions = [];
      }
      this.loadPortrait();
    },
    buildParams() {
      const params = {};
      if (this.courseSerialnumber) {
        params.courseSerialnumber = this.courseSerialnumber;
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = `${this.dateRange[0]} 00:00:00`;
        params.endTime = `${this.dateRange[1]} 23:59:59`;
      }
      return params;
    },
    async loadPortrait() {
      this.loading = true;
      try {
        const res = await getMyPortrait(this.buildParams());
        if (res.code === 200) {
          this.portrait = res.data || null;
        } else {
          this.portrait = null;
          this.$message.error(res.message || "学情画像加载失败");
        }
      } catch (error) {
        this.portrait = null;
        this.$message.error("学情画像加载失败");
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.renderTrendChart();
        });
      }
    },
    renderTrendChart() {
      const el = this.$refs.trendChart;
      if (!el || !this.portrait) {
        this.disposeTrendChart();
        return;
      }
      if (!this.trendChart) {
        this.trendChart = echarts.init(el);
      }
      const trend = this.trendDisplayList || [];
      const dates = trend.map(item => item.date || "");
      const practiceCounts = trend.map(item => Number(item.practiceCount || 0));
      const selfTestCounts = trend.map(item => Number(item.selfTestCount || 0));
      const newWrongCounts = trend.map(item => Number(item.newWrongCount || 0));
      const correctionCounts = trend.map(item =>
        Number(item.correctionCount || 0)
      );
      this.trendChart.setOption(
        {
          color: ["#409eff", "#67c23a", "#e6a23c", "#f56c6c"],
          tooltip: { trigger: "axis" },
          legend: {
            top: 0,
            data: ["刷题数", "自测次数", "新增错题", "订正记录"]
          },
          grid: {
            left: 16,
            right: 16,
            top: 36,
            bottom: 16,
            containLabel: true
          },
          xAxis: {
            type: "category",
            boundaryGap: false,
            data: dates
          },
          yAxis: {
            type: "value",
            minInterval: 1
          },
          series: [
            {
              name: "刷题数",
              type: "line",
              smooth: true,
              data: practiceCounts
            },
            {
              name: "自测次数",
              type: "line",
              smooth: true,
              data: selfTestCounts
            },
            {
              name: "新增错题",
              type: "line",
              smooth: true,
              data: newWrongCounts
            },
            {
              name: "订正记录",
              type: "line",
              smooth: true,
              data: correctionCounts
            }
          ]
        },
        true
      );
    },
    resizeTrendChart() {
      if (this.trendChart) {
        this.trendChart.resize();
      }
    },
    disposeTrendChart() {
      if (this.trendChart) {
        this.trendChart.dispose();
        this.trendChart = null;
      }
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = `${date.getMonth() + 1}`.padStart(2, "0");
      const d = `${date.getDate()}`.padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    formatPercent(value) {
      const num = Number(value || 0);
      return `${num.toFixed(2)}%`;
    },
    formatRatio(value) {
      const num = Number(value || 0) * 100;
      return `${num.toFixed(2)}%`;
    },
    formatAiSuggestion(content) {
      const text = String(content || "暂无 AI 诊断建议");
      const escaped = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
      return escaped
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br/>");
    },
    diagnosisTagType(level) {
      if (level === "HIGH") return "danger";
      if (level === "MEDIUM") return "warning";
      return "info";
    },
    aiTagType(aiDiagnosis) {
      const status = aiDiagnosis && aiDiagnosis.status;
      if (status === "SUCCESS") return "success";
      if (status === "FAILED") return "warning";
      return "info";
    }
  }
};
</script>

<style scoped lang="scss">
.portrait-page {
  --bg-0: #f4f7fc;
  --bg-1: #edf2fb;
  --ink-1: #0f172a;
  --ink-2: #334155;
  --ink-3: #64748b;
  --line: #dbe5f4;
  --surface: rgba(255, 255, 255, 0.92);
  --blue: #2f6fed;
  --green: #22a06b;
  --orange: #dd8f2f;
  --red: #d94848;

  position: relative;
  overflow: hidden;
  background: radial-gradient(circle at 95% 0%, #dce9ff 0%, transparent 36%),
    linear-gradient(180deg, var(--bg-0) 0%, var(--bg-1) 100%);
  min-height: calc(100vh - 48px);
  padding: 20px 18px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.portrait-page::before,
.portrait-page::after {
  content: "";
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
  z-index: 0;
}

.portrait-page::before {
  width: 360px;
  height: 360px;
  top: -170px;
  right: -120px;
  background: rgba(78, 126, 230, 0.16);
}

.portrait-page::after {
  width: 260px;
  height: 260px;
  left: -120px;
  bottom: -110px;
  background: rgba(16, 185, 129, 0.1);
}

.portrait-header {
  position: relative;
  z-index: 1;
  background: linear-gradient(125deg, #122247 0%, #244b89 62%, #2e78ce 100%);
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-shadow: 0 16px 30px rgba(21, 39, 78, 0.26);
}

.title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title-eyebrow {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.78);
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.title {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.title-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.78);
}

.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.portrait-header ::v-deep .el-input__inner,
.portrait-header ::v-deep .el-range-input,
.portrait-header ::v-deep .el-range-separator {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
}

.portrait-header ::v-deep .el-input__inner::placeholder {
  color: rgba(255, 255, 255, 0.72);
}

.portrait-header ::v-deep .el-select .el-input .el-select__caret {
  color: rgba(255, 255, 255, 0.82);
}

.portrait-header ::v-deep .el-date-editor .el-range__icon,
.portrait-header ::v-deep .el-date-editor .el-range__close-icon {
  color: rgba(255, 255, 255, 0.8);
}

.portrait-header ::v-deep .el-button--primary {
  background: #fff;
  border-color: #fff;
  color: #1b3d75;
  font-weight: 700;
}

.section {
  position: relative;
  z-index: 1;
  background: var(--surface);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 14px;
  padding: 16px 18px;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 20px rgba(24, 39, 75, 0.08);
  animation: portrait-rise 0.45s ease both;
}

.section:nth-of-type(2) {
  animation-delay: 0.04s;
}

.section:nth-of-type(3) {
  animation-delay: 0.08s;
}

.section:nth-of-type(4) {
  animation-delay: 0.12s;
}

.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.section-scroll {
  height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
}

.section-scroll::-webkit-scrollbar {
  width: 6px;
}

.section-scroll::-webkit-scrollbar-thumb {
  background: #bfd0eb;
  border-radius: 999px;
}

.section-scroll::-webkit-scrollbar-track {
  background: #eef3fb;
}

.section-title {
  position: relative;
  padding-left: 12px;
  font-size: 16px;
  font-weight: 700;
  color: var(--ink-1);
}

.section-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 16px;
  border-radius: 999px;
  background: linear-gradient(180deg, #2f6fed 0%, #54a2ff 100%);
}

.section-note {
  font-size: 12px;
  color: var(--ink-3);
}

.trend-chart {
  width: 100%;
  height: 336px;
}

.trend-shell {
  border: 1px solid #dbe6f8;
  border-radius: 12px;
  background: linear-gradient(180deg, #f9fbff 0%, #f1f6fe 100%);
  padding: 8px;
}

.sub-title {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed var(--line);
}

.summary-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.summary-main,
.summary-ai {
  min-width: 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.summary-ai {
  border: 1px solid #d6e4ff;
  border-radius: 12px;
  background: linear-gradient(135deg, #edf4ff 0%, #f6f9ff 100%);
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;
}

.summary-ai-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink-1);
}

.summary-ai-status {
  min-height: 22px;
}

.summary-ai-content {
  color: var(--ink-2);
  font-size: 13px;
  line-height: 1.75;
  white-space: pre-wrap;
}

.summary-ai-content ::v-deep strong {
  font-family: "SimHei", "Heiti SC", "Microsoft YaHei", sans-serif;
  font-weight: 700;
  color: #111827;
}

.metric-item {
  border: 1px solid #d8e3f3;
  border-radius: 10px;
  padding: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 4px 10px rgba(46, 77, 130, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 14px rgba(46, 77, 130, 0.1);
  }

  span {
    font-size: 12px;
    color: var(--ink-3);
  }

  strong {
    font-size: 20px;
    color: var(--ink-1);
    font-family: "DIN Alternate", "Segoe UI", "PingFang SC", sans-serif;
    line-height: 1.1;
  }
}

.metric-item--risk strong {
  color: #c53f3f;
}

.task-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.task-item {
  border: 1px solid #d8e3f3;
  border-left: 4px solid #9eb3d7;
  border-radius: 10px;
  padding: 11px 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.7;
  box-shadow: 0 5px 12px rgba(47, 78, 131, 0.06);
}

.task-item--practice {
  border-left-color: var(--blue);
}

.task-item--self_test {
  border-left-color: var(--green);
}

.task-item--correction {
  border-left-color: var(--orange);
}

.task-item--exam {
  border-left-color: var(--red);
}

.task-name {
  font-weight: 700;
  margin-bottom: 4px;
  color: var(--ink-1);
}

.diagnosis-list,
.hint-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.diagnosis-item,
.hint-item {
  border: 1px solid #d8e3f3;
  border-radius: 10px;
  padding: 10px 12px;
  background: #f8fbff;
  display: flex;
  align-items: center;
  gap: 10px;
}

.diagnosis-title {
  font-weight: 700;
  color: var(--ink-1);
}

.diagnosis-desc,
.hint-item span {
  color: var(--ink-2);
  font-size: 13px;
}

.ai-box {
  border: 1px solid #d6e4ff;
  border-radius: 10px;
  background: linear-gradient(135deg, #edf4ff 0%, #f6f9ff 100%);
  padding: 12px;
}

.ai-status {
  margin-bottom: 8px;
  color: var(--ink-2);
  font-size: 13px;
}

.ai-content {
  color: var(--ink-2);
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
}

.section--weak ::v-deep .el-table {
  border-radius: 10px;
  overflow: hidden;
}

.section--weak ::v-deep .el-table th {
  background: #edf3ff;
  color: #30486f;
  font-weight: 700;
}

.section--weak ::v-deep .el-table--border,
.section--weak ::v-deep .el-table--group {
  border-color: #d8e3f3;
}

.section--weak ::v-deep .el-table td,
.section--weak ::v-deep .el-table th.is-leaf {
  border-color: #e2eaf7;
}

.section--weak ::v-deep .el-table tbody tr:hover > td {
  background: #f4f8ff;
}

@keyframes portrait-rise {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1200px) {
  .summary-layout {
    grid-template-columns: 1fr;
  }

  .task-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .portrait-page {
    padding: 14px 12px 22px;
  }

  .portrait-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 14px 14px 16px;
  }

  .title {
    font-size: 21px;
  }

  .filters {
    width: 100%;
  }

  .filters ::v-deep .el-select,
  .filters ::v-deep .el-date-editor {
    width: 100% !important;
  }

  .section {
    padding: 14px 12px;
  }

  .section-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .summary-grid,
  .task-grid {
    grid-template-columns: 1fr;
  }

  .diagnosis-item,
  .hint-item {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
